import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { buildA4PreviewDocument } from './a4DocumentTemplate';

const PREVIEW_UPDATE_DELAY_MS = 350;

const useStyles = makeStyles(({ palette: { primary } }) => ({
  root: {
    backgroundColor: primary.bluish9,
    height: '100%',
    minHeight: 0,
    width: '100%',
  },
  iframe: {
    border: 'none',
    display: 'block',
    height: '100%',
    opacity: 1,
    transition: 'opacity 120ms ease-in-out',
    width: '100%',
  },
  iframeLoading: {
    opacity: 0.6,
  },
}));

const scrollPreviewToPage = (iframe, pageIndex) => {
  const doc = iframe?.contentDocument;
  const pagesRoot = doc?.getElementById('pages-root');
  const safeIndex = Math.max(0, Number(pageIndex) || 0);
  const targetPage = pagesRoot?.children?.[safeIndex];

  if (targetPage?.scrollIntoView) {
    targetPage.scrollIntoView({ block: 'start', inline: 'nearest' });
  }
};

const A4DocumentPreview = ({
  content = '',
  activePageIndex = 0,
  className = null,
}) => {
  const classes = useStyles();
  const iframeRef = useRef(null);
  const activePageRef = useRef(activePageIndex);
  const [debouncedContent, setDebouncedContent] = useState(content);
  const [isUpdating, setIsUpdating] = useState(false);

  activePageRef.current = activePageIndex;

  useEffect(() => {
    setIsUpdating(true);
    const timer = setTimeout(() => {
      setDebouncedContent(content);
      setIsUpdating(false);
    }, PREVIEW_UPDATE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [content]);

  const srcDoc = useMemo(
    () => buildA4PreviewDocument(debouncedContent),
    [debouncedContent]
  );

  const handleIframeLoad = useCallback(() => {
    scrollPreviewToPage(iframeRef.current, activePageRef.current);
  }, []);

  useEffect(() => {
    scrollPreviewToPage(iframeRef.current, activePageIndex);
  }, [activePageIndex]);

  return (
    <div className={classNames(classes.root, className)}>
      <iframe
        ref={iframeRef}
        className={classNames(classes.iframe, {
          [classes.iframeLoading]: isUpdating,
        })}
        srcDoc={srcDoc}
        title="A4 document preview"
        onLoad={handleIframeLoad}
      />
    </div>
  );
};

A4DocumentPreview.propTypes = {
  content: PropTypes.string,
  activePageIndex: PropTypes.number,
  className: PropTypes.string,
};

export default A4DocumentPreview;
export {
  A4_DOCUMENT_TEMPLATE,
  buildA4PreviewDocument,
} from './a4DocumentTemplate';
